import { html, LitElement, type TemplateResult } from 'lit'
import {
  COMPOSER_INSERT_MENTION_EVENT,
  type ComposerInsertDetail
} from '../../hooks/quill_composer/types'
import styles from './mention.css' with { type: 'css' }

/**
 * Shadow-DOM mention chip. Await updateComplete after connecting or changing
 * attributes before reading its rendered content.
 *
 * @csspart base - The chip's primary link or label container.
 * @csspart link - Backwards-compatible chip container.
 * @csspart label - The @-prefixed label.
 * @cssproperty --color-brand-1 - Chip background tint.
 * @cssproperty --color-brand-2 - Hover background tint.
 * @cssproperty --color-brand-9 - Chip text.
 */
export class FacadeMention extends LitElement {
  static tagName = 'facade-mention'
  static override styles = styles

  static override properties = {
    schema: { type: String },
    uuid: { type: String },
    label: { type: String },
    href: { type: String }
  }

  // Lit owns these mutable reactive properties; removed attributes become null.
  schema: string | null = ''
  uuid: string | null = ''
  label: string | null = ''
  href: string | null = ''

  // Bind host clicks once per connected lifetime.
  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('click', this._onClick)
  }

  // Detached chips must not send insertion events.
  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListener('click', this._onClick)
  }

  // Preserve native navigation unless a currently focused composer accepts it.
  private _onClick = (e: MouseEvent): void => {
    if (this.closest('.ql-editor')) {
      return
    }

    const focusedEditor = document.activeElement?.closest('.ql-editor')

    if (!focusedEditor) {
      return
    }

    e.preventDefault()

    const editorId = focusedEditor.parentElement?.id ?? undefined

    const detail: ComposerInsertDetail = {
      schema: this.schema ?? '',
      uuid: this.uuid ?? '',
      label: this.label ?? '',
      editorId
    }

    window.dispatchEvent(
      new CustomEvent(COMPOSER_INSERT_MENTION_EVENT, { detail })
    )
  }

  // Render from the reactive attributes without touching Quill's light DOM.
  override render(): TemplateResult {
    const label = this.label ?? ''
    const href = this.href ?? ''

    if (href) {
      return html`<a part="base link" href="${href}"
          ><span part="label">@${label}</span></a
        >`
    }

    return html`<span part="base link"
        ><span part="label">@${label}</span></span
      >`
  }
}

// Module setup ----------------------------------------------------------------

customElements.define(FacadeMention.tagName, FacadeMention)

export default FacadeMention

declare global {
  interface HTMLElementTagNameMap {
    'facade-mention': FacadeMention
  }
}
